import Button from "components/form/Button";
import React from "react";
import { useTestAttrsFactory } from "utils/testid";

interface ESignConsentProps {
  companyName: string;
  customerEmail: string;
  supportPhone: string;
  supportEmail: string;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  onCancel: () => void;
  onContinue: () => void;
}

const CONSENT_LABEL =
  "I consent to receive records electronically and to sign electronically, and I confirm that I am able to open PDF files on this device.";

/**
 * E-SIGN consent, shown in the signature popup before the Signature Wizard.
 * The customer must accept it before signing electronically; the wording is
 * the approved copy from the CP default-values review doc (section 8, consent
 * screen), with the account's own company and contact details filled in.
 */
const ESignConsent = ({
  companyName,
  customerEmail,
  supportPhone,
  supportEmail,
  checked,
  setChecked,
  onCancel,
  onContinue,
}: ESignConsentProps) => {
  const testAttrs = useTestAttrsFactory();

  const company = companyName || "the company";
  // A phone number must never wrap across lines.
  const phone = <span className="whitespace-nowrap">{supportPhone}</span>;

  const callOrEmail =
    supportPhone && supportEmail ? (
      <>
        by calling {phone} or emailing {supportEmail}
      </>
    ) : supportPhone ? (
      <>by calling {phone}</>
    ) : supportEmail ? (
      `by emailing ${supportEmail}`
    ) : (
      "by contacting us"
    );

  const sections: { title?: string; body: React.ReactNode }[] = [
    {
      body: "Before signing, you must review and accept the following terms governing electronic records and signatures.",
    },
    {
      title: "Electronic Signature.",
      body: "You may sign this Agreement electronically. Your electronic signature has the same legal effect as a handwritten signature.",
    },
    {
      title: "Electronic Records.",
      body: `Upon your consent, ${company} will provide this Agreement, payment confirmations and related notices electronically, in the portal and by email${
        customerEmail ? ` to ${customerEmail}` : ""
      }. This consent applies to this Agreement and the records related to it.`,
    },
    {
      title: "Changes to Contact Information.",
      body: (
        <>
          You may update your email address under My Profile in the portal
          {supportPhone ? <> or by calling {phone}</> : null}.
        </>
      ),
    },
    {
      title: "Paper Copies and Withdrawal of Consent.",
      body: (
        <>
          You may request a paper copy of any record at no charge, or withdraw
          this consent at any time, {callOrEmail}. If you withdraw consent,{" "}
          {company} will send future records to your mailing address in paper
          form. Withdrawal of consent does not affect payments you have already
          authorized or records already provided.
        </>
      ),
    },
    {
      title: "System Requirements.",
      body: "A device with internet access, a current web browser, software capable of opening PDF files, and the ability to save or print them.",
    },
  ];

  return (
    <div {...testAttrs("esign-consent")}>
      {/* The terms scroll on their own; the checkbox and buttons stay pinned
          below them so the customer never has to reach the end to find them. */}
      <div
        className="mt-4 max-h-[250px] overflow-y-auto pr-3 flex flex-col gap-[10px] text-sm leading-[21px] text-txtParagraph"
        tabIndex={0}
        aria-label="Consent terms"
      >
        {sections.map((section, index) => (
          <p key={index} className="break-words">
            {section.title ? (
              <span className="font-medium text-txtPrimary">
                {section.title}{" "}
              </span>
            ) : null}
            {section.body}
          </p>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-strokePrimary flex gap-[10px] items-start">
        <input
          id="esign-consent-checkbox"
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="dcdial_portal_checkbox mt-1 cursor-pointer shrink-0"
          {...testAttrs("esign-consent-checkbox")}
        />
        <label
          htmlFor="esign-consent-checkbox"
          className="break-words text-sm leading-[21px] text-txtPrimary cursor-pointer"
        >
          {CONSENT_LABEL}
        </label>
      </div>

      <div className="w-full pt-5 flex lg:justify-end gap-5">
        <Button
          text="Cancel"
          variant="stroke-outline"
          onClick={onCancel}
          className="lg:w-fit w-full"
          {...testAttrs("esign-consent-cancel", "Cancel consent")}
        />
        <Button
          text="Continue"
          variant="variable"
          isDisabled={!checked}
          onClick={onContinue}
          className="lg:w-fit w-full"
          {...testAttrs("esign-consent-continue", "Continue to signature")}
        />
      </div>

      <p className="pt-3 text-xs leading-[18px] text-txtSecondary">
        {supportPhone ? (
          <>To receive a paper agreement instead, call {phone}.</>
        ) : (
          "To receive a paper agreement instead, contact us."
        )}
      </p>
    </div>
  );
};

export default ESignConsent;
